import { ChamferFeature } from '@kb3d/models/classes/features/chamfer-feature';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { FeatureGeometryProcessor, ProcessorData } from './base-processor';

type Vec3 = [number, number, number];

export class ChamferGeometryProcessor extends FeatureGeometryProcessor<ChamferFeature> {
    updateMeshGeometry(
        feature: ChamferFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>,
        dependencyData: ProcessorData<never>
    ): Partial<SubmeshVertexData> {
        // Hydrate a new working container from the base pipeline
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges, dependencyData);

        // If vertexDataChanges already contains geometry from a previous feature in the pipeline,
        // use those values so chamfers can be stacked on top of other features.
        if ((!newVertexData.positions || newVertexData.positions.length === 0) && vertexDataChanges.positions && vertexDataChanges.positions.length) {
            newVertexData.positions = vertexDataChanges.positions as Float32Array;
        }
        if ((!newVertexData.indices || newVertexData.indices.length === 0) && vertexDataChanges.indices && vertexDataChanges.indices.length) {
            newVertexData.indices = vertexDataChanges.indices as any;
        }
        if ((!newVertexData.normals || newVertexData.normals.length === 0) && vertexDataChanges.normals && vertexDataChanges.normals.length) {
            newVertexData.normals = vertexDataChanges.normals as Float32Array;
        }
        if ((!newVertexData.uvs || newVertexData.uvs.length === 0) && vertexDataChanges.uvs && vertexDataChanges.uvs.length) {
            newVertexData.uvs = vertexDataChanges.uvs as Float32Array;
        }

        // Hydrate from base data if changes container came empty in this frame
        if ((!newVertexData.positions || newVertexData.positions.length === 0) && vertexData.positions && vertexData.positions.length) {
            newVertexData.positions = vertexData.positions;
        }
        if ((!newVertexData.indices || newVertexData.indices.length === 0) && vertexData.indices && vertexData.indices.length) {
            newVertexData.indices = vertexData.indices;
        }
        if ((!newVertexData.normals || newVertexData.normals.length === 0) && vertexData.normals && vertexData.normals.length) {
            newVertexData.normals = vertexData.normals;
        }
        if ((!newVertexData.uvs || newVertexData.uvs.length === 0) && vertexData.uvs && vertexData.uvs.length) {
            newVertexData.uvs = vertexData.uvs;
        }

        // Guards
        if (!newVertexData.positions || !newVertexData.indices) return newVertexData;
        if (newVertexData.positions.length === 0 || newVertexData.indices.length === 0) return newVertexData;

        // Working copies
        const positions = Array.from(newVertexData.positions);
        const indices = Array.from(newVertexData.indices);

        // Build a boundary loop from the selection
        const loopResult = this._buildQuadLoopFromVertexGroups(indices, feature.vertexGroups);

        // Accept any valid boundary with at least a triangle (supports n-gons)
        if (!loopResult || loopResult.loop.length < 3) {
            return newVertexData;
        }

        // When a fan-triangulated face (hasHub) is missing its closing triangle from the
        // vertex group selection, the boundary walk produces a palindrome of length
        // 2*selectedTris instead of a proper closed ring of selectedTris vertices.
        // Extract just the unique forward-pass half to restore the correct perimeter.
        const rawLoop = loopResult.loop;
        const loop =
            loopResult.hasHub && rawLoop.length === 2 * loopResult.selectedTriIds.size
                ? rawLoop.slice(0, loopResult.selectedTriIds.size)
                : rawLoop;

        const selectedTriIds = loopResult.selectedTriIds;

        // Compute 3D positions and centroid for the selected boundary loop vertices
        const loopPts = loop.map((vi) => this._v3(positions, vi));
        const centroid = this._centroid(loopPts);

        // Base normal from the original face (first two edges of the loop)
        const p0 = this._v3(positions, loop[0]);
        const p1 = this._v3(positions, loop[1]);
        const p2 = this._v3(positions, loop[2]);
        const ux0 = p1[0] - p0[0], uy0 = p1[1] - p0[1], uz0 = p1[2] - p0[2];
        const vx0 = p2[0] - p0[0], vy0 = p2[1] - p0[1], vz0 = p2[2] - p0[2];
        const bnX = uy0 * vz0 - uz0 * vy0;
        const bnY = uz0 * vx0 - ux0 * vz0;
        const bnZ = ux0 * vy0 - uy0 * vx0;
        const bnLen = Math.hypot(bnX, bnY, bnZ) || 1;
        let baseN: Vec3 = [bnX / bnLen, bnY / bnLen, bnZ / bnLen];

        // Compute mesh bounding box center to decide outward direction
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        for (let i = 0; i < positions.length; i += 3) {
            const x = positions[i + 0], y = positions[i + 1], z = positions[i + 2];
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
            if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
        }
        const meshCenter: Vec3 = [(minX + maxX) * 0.5, (minY + maxY) * 0.5, (minZ + maxZ) * 0.5];

        // Force baseN to point outward relative to the model center
        const toOut: Vec3 = [centroid[0] - meshCenter[0], centroid[1] - meshCenter[1], centroid[2] - meshCenter[2]];
        const dotOut = baseN[0] * toOut[0] + baseN[1] * toOut[1] + baseN[2] * toOut[2];
        if (dotOut < 0) {
            baseN = [-baseN[0], -baseN[1], -baseN[2]];
        }

        // Remove the original face triangles using the selectedTriIds from the selection
        // (works for any face topology: quads, circles, n-gons with center vertex, etc.)
        const keptIdx: number[] = [];
        for (let i = 0; i < indices.length; i += 3) {
            const triId = Math.floor(i / 3);
            if (!selectedTriIds.has(triId)) {
                keptIdx.push(indices[i], indices[i + 1], indices[i + 2]);
            }
        }

        // Duplicate outer ring vertices (hard edge)
        const outPos = positions.slice();
        const dupOuterIdx: number[] = [];
        for (const vi of loop) {
            const base = vi * 3;
            const id = outPos.length / 3;
            outPos.push(positions[base + 0], positions[base + 1], positions[base + 2]);
            dupOuterIdx.push(id);
        }

        // Create inner ring vertices for wall quad construction
        const insetIdx: number[] = [];

        // chamferSize controls the inset of the inner ring (the width of the chamfer band):
        // - chamferSize = 0: no inset, inner ring matches the outer ring (no visible chamfer)
        // - chamferSize = 1: maximum inset, inner ring is at the centroid (largest chamfer band)
        // Formula: offset = maxOffset * chamferSize

        // Compute min distance from any loop vertex to centroid (geometry-safe maximum inset)
        const minDistToCentroid = loopPts.reduce((min, pt) => {
            const d = Math.hypot(pt[0] - centroid[0], pt[1] - centroid[1], pt[2] - centroid[2]);
            return Math.min(min, d);
        }, Infinity);

        const maxOffset = minDistToCentroid * 0.99;
        const chamferSizeValue = Math.max(0, Number(feature.chamferSize ?? 0));
        // Direct mapping: chamferSize=0 produces no inset (no chamfer), chamferSize=1 produces maximum inset
        const offset = maxOffset * chamferSizeValue;

        for (const vi of loop) {
            const P = this._v3(positions, vi);

            // Direction from centroid to vertex (outward)
            let dx = P[0] - centroid[0];
            let dy = P[1] - centroid[1];
            let dz = P[2] - centroid[2];
            const len = Math.hypot(dx, dy, dz) || 1;
            dx /= len; dy /= len; dz /= len;

            // Move inward toward centroid (inset)
            const newP: Vec3 = [
                P[0] - dx * offset,
                P[1] - dy * offset,
                P[2] - dz * offset,
            ];

            const id = outPos.length / 3;
            outPos.push(newP[0], newP[1], newP[2]);
            insetIdx.push(id);
        }

        // Offset inner ring vertices along the face normal by chamferProtusion.
        // chamferProtusion accepts any value: positive protrudes outward, negative recedes inward.
        const protrude = Number((feature as any).chamferProtusion ?? 0);

        if (Math.abs(protrude) > 1e-8) {
            for (const id of insetIdx) {
                const b = id * 3;
                outPos[b + 0] += baseN[0] * protrude;
                outPos[b + 1] += baseN[1] * protrude;
                outPos[b + 2] += baseN[2] * protrude;
            }
        }

        // Duplicate the inner ring into separate vertex buffers so each cap face has
        // independent normals (prevents shading bleed between the cap and the walls).

        // --- OUTER CAP (independent normals) ---
        const capInsetIdx: number[] = [];
        for (const id of insetIdx) {
            const b = id * 3;
            const capId = outPos.length / 3;
            outPos.push(
                outPos[b + 0],
                outPos[b + 1],
                outPos[b + 2]
            );
            capInsetIdx.push(capId);
        }

        // --- INNER CAP (independent normals) ---
        const bottomCapIdx: number[] = [];
        for (const id of insetIdx) {
            const b = id * 3;
            const newId = outPos.length / 3;
            outPos.push(
                outPos[b + 0],
                outPos[b + 1],
                outPos[b + 2]
            );
            bottomCapIdx.push(newId);
        }

        // Force ring orientation to face baseN (outer and inner in sync)
        const ringDot = (() => {
            if (dupOuterIdx.length < 3) return 1;
            const i0 = dupOuterIdx[0];
            const i1 = dupOuterIdx[1];
            const i2 = dupOuterIdx[2];

            const ax = outPos[i0 * 3 + 0], ay = outPos[i0 * 3 + 1], az = outPos[i0 * 3 + 2];
            const bx = outPos[i1 * 3 + 0], by = outPos[i1 * 3 + 1], bz = outPos[i1 * 3 + 2];
            const cx = outPos[i2 * 3 + 0], cy = outPos[i2 * 3 + 1], cz = outPos[i2 * 3 + 2];

            const ux = bx - ax, uy = by - ay, uz = bz - az;
            const vx = cx - ax, vy = cy - ay, vz = cz - az;

            const nx = uy * vz - uz * vy;
            const ny = uz * vx - ux * vz;
            const nz = ux * vy - uy * vx;

            return nx * baseN[0] + ny * baseN[1] + nz * baseN[2];
        })();

        // If the traced loop is reversed relative to baseN, reverse both rings
        if (ringDot < 0) {
            dupOuterIdx.reverse();
            insetIdx.reverse();
            capInsetIdx.reverse();
            bottomCapIdx.reverse();
        }

        // Build wall quads
        const outIdx = keptIdx.slice();
        const m = loop.length;

        // Helper: clone a vertex from the current position buffer and return the new vertex index
        const cloneV = (srcId: number) => {
            const b = srcId * 3;
            const newId = outPos.length / 3;
            outPos.push(outPos[b + 0], outPos[b + 1], outPos[b + 2]);
            return newId;
        };

        for (let i = 0; i < m; i++) {
            const aO = dupOuterIdx[i];
            const bO = dupOuterIdx[(i + 1) % m];
            const aI = insetIdx[i];
            const bI = insetIdx[(i + 1) % m];

            // Break smoothing between quads: clone 4 vertices for this quad
            const aOq = cloneV(aO);
            const bOq = cloneV(bO);
            const aIq = cloneV(aI);
            const bIq = cloneV(bI);

            // Determine correct triangle winding for this wall quad by comparing the
            // candidate normal against the expected outward-facing direction
            const ax = outPos[aOq * 3 + 0], ay = outPos[aOq * 3 + 1], az = outPos[aOq * 3 + 2];
            const bx = outPos[bOq * 3 + 0], by = outPos[bOq * 3 + 1], bz = outPos[bOq * 3 + 2];
            const aix = outPos[aIq * 3 + 0], aiy = outPos[aIq * 3 + 1], aiz = outPos[aIq * 3 + 2];
            const bix = outPos[bIq * 3 + 0], biy = outPos[bIq * 3 + 1], biz = outPos[bIq * 3 + 2];

            // edge tangent (outer ring)
            let tx = bx - ax, ty = by - ay, tz = bz - az;
            const tlen = Math.hypot(tx, ty, tz) || 1;
            tx /= tlen; ty /= tlen; tz /= tlen;

            // outward direction in face plane (centroid to outer edge midpoint)
            const mx = 0.5 * (ax + bx), my = 0.5 * (ay + by), mz = 0.5 * (az + bz);
            let ox = mx - centroid[0], oy = my - centroid[1], oz = mz - centroid[2];
            const olen = Math.hypot(ox, oy, oz) || 1;
            ox /= olen; oy /= olen; oz /= olen;

            // target wall normal approx cross(baseN, tangent), aligned to outward vector o
            let txn = baseN[1] * tz - baseN[2] * ty;
            let tyn = baseN[2] * tx - baseN[0] * tz;
            let tzn = baseN[0] * ty - baseN[1] * tx;
            if (txn * ox + tyn * oy + tzn * oz < 0) { txn = -txn; tyn = -tyn; tzn = -tzn; }

            // candidate wall triangle normal for (aO to aI to bI)
            const u1x = aix - ax, u1y = aiy - ay, u1z = aiz - az;
            const v1x = bix - ax, v1y = biy - ay, v1z = biz - az;
            const n1x = u1y * v1z - u1z * v1y;
            const n1y = u1z * v1x - u1x * v1z;
            const n1z = u1x * v1y - u1y * v1x;

            // choose winding that aligns with target
            const s = n1x * txn + n1y * tyn + n1z * tzn;

            if (s < 0) {
                // keep original winding
                outIdx.push(aOq, aIq, bIq);
                outIdx.push(aOq, bIq, bOq);
            } else {
                // flip this quad winding
                outIdx.push(aOq, bOq, bIq);
                outIdx.push(aOq, bIq, aIq);
            }

        }

        // Computes the centroid of a ring and appends it as a new vertex, returning its index.
        const addRingCentroid = (ring: number[]): number => {
            let cx = 0, cy = 0, cz = 0;
            for (const id of ring) {
                cx += outPos[id * 3 + 0];
                cy += outPos[id * 3 + 1];
                cz += outPos[id * 3 + 2];
            }
            const n = ring.length;
            const newId = outPos.length / 3;
            outPos.push(cx / n, cy / n, cz / n);
            return newId;
        };

        // Build the outer cap face using a centroid fan for correct n-gon triangulation
        const capStart = outIdx.length;
        if (capInsetIdx.length >= 3) {
            const centerV = addRingCentroid(capInsetIdx);
            for (let i = 0; i < capInsetIdx.length; i++) {
                const a = capInsetIdx[i];
                const b = capInsetIdx[(i + 1) % capInsetIdx.length];
                outIdx.push(centerV, a, b);
            }
            // Flip only the cap fan if its normal disagrees with baseN
            if (outIdx.length > capStart) {
                const ia = outIdx[capStart + 0];
                const ib = outIdx[capStart + 1];
                const ic = outIdx[capStart + 2];

                const ax = outPos[ia * 3 + 0], ay = outPos[ia * 3 + 1], az = outPos[ia * 3 + 2];
                const bx = outPos[ib * 3 + 0], by = outPos[ib * 3 + 1], bz = outPos[ib * 3 + 2];
                const cx = outPos[ic * 3 + 0], cy = outPos[ic * 3 + 1], cz = outPos[ic * 3 + 2];

                const ux = bx - ax, uy = by - ay, uz = bz - az;
                const vx = cx - ax, vy = cy - ay, vz = cz - az;

                const nx = uy * vz - uz * vy;
                const ny = uz * vx - ux * vz;
                const nz = ux * vy - uy * vx;

                const d = nx * baseN[0] + ny * baseN[1] + nz * baseN[2];
                if (d < 0) {
                    for (let k = capStart; k < outIdx.length; k += 3) {
                        const tmp = outIdx[k + 1];
                        outIdx[k + 1] = outIdx[k + 2];
                        outIdx[k + 2] = tmp;
                    }
                }
            }
        }

        // Build the wall-facing side of the inner ring cap (inward-facing, reversed winding)
        let centerVb = -1;
        if (bottomCapIdx.length >= 3) {
            centerVb = addRingCentroid(bottomCapIdx);
            for (let i = 0; i < bottomCapIdx.length; i++) {
                const a = bottomCapIdx[i];
                const b = bottomCapIdx[(i + 1) % bottomCapIdx.length];
                outIdx.push(centerVb, b, a);   // inward-facing (reversed winding)
            }
        }

        // Write final position and index buffers to the output vertex data
        newVertexData.positions = new Float32Array(outPos);
        newVertexData.indices = new Int32Array(outIdx);

        // Recompute vertex normals from triangle geometry (area-weighted accumulation, then normalize)
        const vCount = outPos.length / 3;
        const nrm = new Float32Array(outPos.length);
        for (let i = 0; i < outIdx.length; i += 3) {
            const ia = outIdx[i], ib = outIdx[i + 1], ic = outIdx[i + 2];

            const ax = outPos[ia * 3 + 0], ay = outPos[ia * 3 + 1], az = outPos[ia * 3 + 2];
            const bx = outPos[ib * 3 + 0], by = outPos[ib * 3 + 1], bz = outPos[ib * 3 + 2];
            const cx = outPos[ic * 3 + 0], cy = outPos[ic * 3 + 1], cz = outPos[ic * 3 + 2];

            const ux = bx - ax, uy = by - ay, uz = bz - az;
            const vx = cx - ax, vy = cy - ay, vz = cz - az;

            const nx = uy * vz - uz * vy;
            const ny = uz * vx - ux * vz;
            const nz = ux * vy - uy * vx;

            nrm[ia * 3 + 0] += nx; nrm[ia * 3 + 1] += ny; nrm[ia * 3 + 2] += nz;
            nrm[ib * 3 + 0] += nx; nrm[ib * 3 + 1] += ny; nrm[ib * 3 + 2] += nz;
            nrm[ic * 3 + 0] += nx; nrm[ic * 3 + 1] += ny; nrm[ic * 3 + 2] += nz;
        }

        // Normalize normals
        for (let i = 0; i < vCount; i++) {
            const x = nrm[i * 3 + 0], y = nrm[i * 3 + 1], z = nrm[i * 3 + 2];
            const len = Math.hypot(x, y, z) || 1;
            nrm[i * 3 + 0] = x / len;
            nrm[i * 3 + 1] = y / len;
            nrm[i * 3 + 2] = z / len;
        }

        // Override normals for the inner cap ring + center so they are exactly -baseN
        // (area-weighted accumulation can blend with adjacent wall faces and tilt them)
        const overrideInward = (ids: number[]) => {
            for (const id of ids) {
                nrm[id * 3 + 0] = -baseN[0];
                nrm[id * 3 + 1] = -baseN[1];
                nrm[id * 3 + 2] = -baseN[2];
            }
        };
        overrideInward(bottomCapIdx);
        if (centerVb >= 0) overrideInward([centerVb]);

        newVertexData.normals = nrm;

        // Project vertex positions onto the dominant face plane axis to generate UVs
        const uv = new Float32Array(vCount * 2);
        const axIdx =
            Math.abs(baseN[0]) >= Math.abs(baseN[1]) && Math.abs(baseN[0]) >= Math.abs(baseN[2]) ? 0 :
                Math.abs(baseN[1]) >= Math.abs(baseN[2]) ? 1 : 2;

        let minU = Infinity, minV = Infinity, maxU = -Infinity, maxV = -Infinity;
        for (let i = 0; i < vCount; i++) {
            const x = outPos[i * 3 + 0], y = outPos[i * 3 + 1], z = outPos[i * 3 + 2];
            let U = 0, V = 0;
            if (axIdx === 0) { U = y; V = z; }
            else if (axIdx === 1) { U = x; V = z; }
            else { U = x; V = y; }
            uv[i * 2 + 0] = U; uv[i * 2 + 1] = V;
            if (U < minU) minU = U; if (U > maxU) maxU = U;
            if (V < minV) minV = V; if (V > maxV) maxV = V;
        }
        const du = Math.max(1e-8, maxU - minU), dv = Math.max(1e-8, maxV - minV);
        for (let i = 0; i < vCount; i++) {
            uv[i * 2 + 0] = (uv[i * 2 + 0] - minU) / du;
            uv[i * 2 + 1] = (uv[i * 2 + 1] - minV) / dv;
        }
        newVertexData.uvs = uv;

        // Define a single submesh entry covering the entire output geometry
        newVertexData.submeshData = [
            {
                materialIndex: 0,
                verticesStart: 0,
                verticesCount: vCount,
                indexStart: 0,
                indexCount: outIdx.length,
            },
        ];

        // Preserve manually computed normals so the engine does not recalculate them
        newVertexData.preserveNormals = true;

        return newVertexData;
    }

    // --- helpers ----------------------------------------------------------------

    private _v3(pos: ArrayLike<number>, i: number): Vec3 {
        const k = i * 3;
        return [pos[k + 0], pos[k + 1], pos[k + 2]];
    }

    private _centroid(points: Vec3[]): Vec3 {
        let x = 0, y = 0, z = 0;
        const n = points.length || 1;
        for (const p of points) {
            x += p[0]; y += p[1]; z += p[2];
        }
        return [x / n, y / n, z / n];
    }

    private _edgeKey(a: number, b: number): string {
        return a < b ? `${a}_${b}` : `${b}_${a}`;
    }

    // Picks a 4-vertex loop if present, else returns the first loop
    private _quadOrFirst(loopsArr: Array<number[]>): number[] | null {
        if (!loopsArr || loopsArr.length === 0) return null;
        const quad = loopsArr.find((l) => l.length === 4);
        return quad || loopsArr[0];
    }

    /**
     * Builds a boundary loop from the selection stored in vertexGroups.
     * Returns the loop vertices and the set of selected triangle IDs.
     * Supports any face topology: quads, triangles, n-gons (circles, cylinders, cones).
     */
    private _buildQuadLoopFromVertexGroups(
        indices: number[],
        vertexGroups: Array<{ startIndex: number; endIndex: number }>
    ): { loop: number[]; selectedTriIds: Set<number>; hasHub: boolean } | null {
        if (!vertexGroups || vertexGroups.length === 0) return null;

        const triCount = Math.floor(indices.length / 3);
        if (triCount <= 0) return null;

        // Collect selected triangle ids
        const selected = new Set<number>();
        for (const g of vertexGroups) {
            const sRaw = Math.max(0, Math.floor(g.startIndex));
            const eRaw = Math.max(sRaw, Math.floor(g.endIndex));
            // Map index-offset domain to triangle domain
            const sT = Math.max(0, Math.floor(sRaw / 3));
            const eT = Math.min(triCount, Math.ceil(eRaw / 3));
            for (let t = sT; t < eT; t++) selected.add(t);
        }

        if (selected.size === 0) return null;

        // Count how many times each edge appears across all selected triangles to find boundary edges
        type EdgeCount = { a: number; b: number; count: number };
        const edgeCounter = new Map<string, EdgeCount>();
        const triVerts: Array<[number, number, number]> = new Array(triCount);
        for (let t = 0; t < triCount; t++) {
            const a = indices[3 * t + 0] as number;
            const b = indices[3 * t + 1] as number;
            const c = indices[3 * t + 2] as number;
            triVerts[t] = [a, b, c];
        }

        // Identify fan-center "hub" vertices: vertices shared by more than 2 selected
        // triangles. A fan-triangulated face (e.g. cone base) has a single center vertex
        // that appears in every triangle. Edges that touch it are internal seam edges,
        // not true perimeter edges -- including them in the boundary loop causes the
        // chamfer to produce a spurious wall quad from the inner ring to the fan center.
        const vertexTriCount = new Map<number, number>();
        for (const t of selected) {
            for (const v of triVerts[t]) {
                vertexTriCount.set(v, (vertexTriCount.get(v) ?? 0) + 1);
            }
        }
        const hubVertices = new Set<number>();
        for (const [v, count] of vertexTriCount) {
            if (count > 2) hubVertices.add(v);
        }

        for (const t of selected) {
            const [a, b, c] = triVerts[t];
            const edges: Array<[number, number]> = [
                [a, b],
                [b, c],
                [c, a],
            ];
            for (const [u, v] of edges) {
                // Skip edges that touch a hub vertex -- they are fan-center seam edges,
                // not part of the true face perimeter.
                if (hubVertices.has(u) || hubVertices.has(v)) continue;
                const key = this._edgeKey(u, v);
                const rec = edgeCounter.get(key) ?? { a: u, b: v, count: 0 };
                rec.count += 1;
                edgeCounter.set(key, rec);
            }
        }

        // Boundary edges are those that occur exactly once
        const boundaryEdges = [...edgeCounter.values()].filter((e) => e.count === 1);
        if (boundaryEdges.length < 3) return null;

        // Build adjacency from boundary edges
        const adj = new Map<number, number[]>();
        const ensure = (k: number) => {
            let arr = adj.get(k);
            if (!arr) {
                arr = [];
                adj.set(k, arr);
            }
            return arr;
        };
        for (const { a, b } of boundaryEdges) {
            ensure(a).push(b);
            ensure(b).push(a);
        }

        // Walk the adjacency map to trace one or more closed boundary loops
        const visited = new Set<number>();
        const loops: Array<number[]> = [];

        const pickStart = () => {
            for (const [v, nbrs] of adj) {
                if (!visited.has(v) && nbrs && nbrs.length > 0) return v;
            }
            return -1;
        };

        let start = pickStart();
        const maxStepsTotal = boundaryEdges.length * 4 + 16;

        while (start !== -1) {
            if (visited.has(start)) {
                start = pickStart();
                continue;
            }

            const loop: number[] = [start];
            visited.add(start);

            let curr = start;
            let prev = -1;
            let steps = 0;
            let closed = false;

            while (steps++ < maxStepsTotal) {
                const nbrs = adj.get(curr) || [];
                let next: number | undefined;

                if (nbrs.length === 1) {
                    next = nbrs[0];
                } else if (nbrs.length >= 2) {
                    next = nbrs[0] !== prev ? nbrs[0] : nbrs[1];
                    if (nbrs.length > 2) {
                        for (let i = 0; i < nbrs.length; i++) {
                            const c = nbrs[i];
                            if (c !== prev) {
                                next = c;
                                break;
                            }
                        }
                    }
                }

                if (next === undefined) break;
                if (next === start) {
                    closed = true;
                    break;
                }

                loop.push(next);
                visited.add(next);
                prev = curr;
                curr = next;
            }

            if (closed && loop.length >= 3) {
                loops.push(loop);
                const maybe = this._quadOrFirst(loops);
                if (maybe && maybe.length === 4) {
                    return { loop: maybe, selectedTriIds: selected, hasHub: hubVertices.size > 0 };
                }
            }

            start = pickStart();
            if (start === -1) {
                const maybe = this._quadOrFirst(loops);
                return maybe ? { loop: maybe, selectedTriIds: selected, hasHub: hubVertices.size > 0 } : null;
            }
        }

        return null;
    }
}